import { gql } from '@apollo/client';
// Fragments
import {
  HostedTicketEventFields,
  MyTicketFields,
  MyTicketOrderFields,
  PublicTicketEventFields,
  TicketOrderFields,
} from './fragments';
import { MatchupFields, MatchupVoteResultsFields } from 'api/matchup/fragments';

export const TICKETING_LAUNCHED = gql`
  query TicketingLaunched {
    ticketingLaunched
  }
`;

export const HOSTED_TICKET_EVENTS = gql`
  query HostedTicketEvents($input: HostedTicketEventsInput!) {
    hostedTicketEvents(input: $input) {
      total
      offset
      limit
      entities {
        ...HostedTicketEventFields
      }
    }
  }
  ${HostedTicketEventFields}
`;

// Aliased to the public place fields so place pickers read either the same way.
export const GET_TICKET_VENUE_PLACE_PREDICTIONS = gql`
  query GetTicketVenuePlacePredictions(
    $input: TicketVenuePlacePredictionsInput!
  ) {
    getPlacePredictions: ticketVenuePlacePredictions(input: $input) {
      entities {
        id
        text
        matches {
          startOffset
          endOffset
        }
      }
    }
  }
`;

export const GET_TICKET_VENUE_PLACE_DETAILS = gql`
  query GetTicketVenuePlaceDetails($input: GetPlaceDetailsInput!) {
    getPlaceDetails: ticketVenuePlaceDetails(input: $input) {
      id
      formattedAddress
      location {
        latitude
        longitude
      }
      country
      countryCode
      state
      stateCode
      city
      name
      addressLine1
      postalCode
      timezone
    }
  }
`;

export const GET_TICKET_EVENTS = gql`
  query GetTicketEvents($input: TicketEventsInput!) {
    ticketEvents(input: $input) {
      total
      offset
      limit
      entities {
        ...PublicTicketEventFields
      }
    }
  }
  ${PublicTicketEventFields}
`;

export const GET_TICKET_EVENT_FILTERS = gql`
  query GetTicketEventFilters {
    ticketEventFilters {
      price
      startsAt
    }
  }
`;

export const GET_TICKET_EVENT = gql`
  query GetTicketEvent($input: TicketEventLookupInput!) {
    ticketEvent(input: $input) {
      ...PublicTicketEventFields
      fanPollingEnabled
      matchups {
        ...MatchupFields
      }
    }
  }
  ${PublicTicketEventFields}
  ${MatchupFields}
`;

/** What the participant invitation page shows of its event. */
export const GET_TICKET_EVENT_INVITE = gql`
  query GetTicketEventInvite($input: TicketEventLookupInput!) {
    ticketEvent(input: $input) {
      id
      slug
      name
      description
      coverImageUrl
      stream {
        id
        slug
        store {
          id
          slug
        }
      }
    }
  }
`;

export const GET_TICKET_EVENT_VOTE_RESULTS = gql`
  query GetTicketEventVoteResults($ticketEventId: String!) {
    getTicketEventVoteResults(ticketEventId: $ticketEventId) {
      ...MatchupVoteResultsFields
    }
  }
  ${MatchupVoteResultsFields}
`;

export const GET_TICKET_AVAILABILITY = gql`
  query GetTicketAvailability($input: TicketAvailabilityInput!) {
    ticketAvailability(input: $input) {
      tierId
      tierLabel
      price
      maxPerOrder
      salesCloseAt
      saleState
      availableAdmissions
    }
  }
`;

export const GET_TICKET_TRANSFER_INVITATION = gql`
  query GetTicketTransferInvitation($input: TicketTransferInvitationInput!) {
    ticketTransferInvitation(input: $input) {
      status
      recipientEmail
      recipientHasAccount
      senderName
      eventName
      eventSlug
      startsAt
      timezone
      venueName
      coverImageUrl
      tierLabel
    }
  }
`;

export const GET_TICKET_QR = gql`
  query GetTicketQr($input: TicketQrInput!) {
    ticketQr(input: $input) {
      token
    }
  }
`;

export const GET_TICKET_EVENT_SCHEDULE_DEFAULTS = gql`
  query GetTicketEventScheduleDefaults {
    ticketEventScheduleDefaults {
      salesCloseAfterStartSeconds
      maxSalesCloseAfterStartSeconds
    }
  }
`;

export const GET_TICKET_SERVICE_FEE = gql`
  query GetTicketServiceFee {
    ticketServiceFee {
      percent
      perTicket
    }
  }
`;

export const GET_TICKET_ORDER = gql`
  query GetTicketOrder($input: TicketOrderInput!) {
    ticketOrder(input: $input) {
      ...TicketOrderFields
    }
  }
  ${TicketOrderFields}
`;

export const GET_MY_TICKET_ORDERS = gql`
  query GetMyTicketOrders($input: MyTicketOrdersInput!) {
    myTicketOrders(input: $input) {
      total
      offset
      limit
      entities {
        ...MyTicketOrderFields
      }
    }
  }
  ${MyTicketOrderFields}
`;

/** Every order id of the buyer, to tell their own tickets from ones sent to them. */
export const GET_MY_TICKET_ORDER_IDS = gql`
  query GetMyTicketOrderIds($input: MyTicketOrdersInput!) {
    myTicketOrders(input: $input) {
      total
      entities {
        id
      }
    }
  }
`;

export const GET_MY_TICKETS = gql`
  query GetMyTickets($input: MyTicketsInput!) {
    myTickets(input: $input) {
      total
      offset
      limit
      entities {
        ...MyTicketFields
      }
    }
  }
  ${MyTicketFields}
`;

export const GET_HOSTED_TICKET_EVENT = gql`
  query GetHostedTicketEvent($input: HostedTicketEventInput!) {
    hostedTicketEvent(input: $input) {
      ...HostedTicketEventFields
      affiliateEnabled
      affiliateCommissionPercent
      venueAddress {
        city
      }
    }
  }
  ${HostedTicketEventFields}
`;

/** Headline sales of the hosted events on one page of a list. */
export const GET_TICKET_EVENT_SALES_SUMMARIES = gql`
  query GetTicketEventSalesSummaries($input: TicketEventSalesSummariesInput!) {
    ticketEventSalesSummaries(input: $input) {
      ticketEventId
      ticketsSold
      maxAdmissions
      ticketRevenue
      refundedOrders
    }
  }
`;

export const GET_TICKET_EVENT_SALES_REPORT = gql`
  query GetTicketEventSalesReport($input: HostedTicketEventInput!) {
    ticketEventSalesReport(input: $input) {
      ticketEventId
      totals {
        currency
        ticketsSold
        ticketRevenue
      }
      byTier {
        tierId
        tierLabel
        price
        capacity
        available
        ticketsSold
        revenue
      }
    }
  }
`;

export const GET_TICKET_EVENT_ORDERS = gql`
  query GetTicketEventOrders($input: TicketEventOrdersInput!) {
    ticketEventOrders(input: $input) {
      total
      offset
      limit
      entities {
        orderId
        reference
        status
        kind
        customerName
        email
        amount
        discountAmount
        promocodeCode
        promocodeDiscountPercent
        cardBrand
        cardLast4
        createdAt
        lines {
          tierLabel
          admissionCount
        }
      }
    }
  }
`;

export const GET_TICKET_EVENT_FOR_EDIT = gql`
  query GetTicketEventForEdit($input: HostedTicketEventInput!) {
    hostedTicketEvent(input: $input) {
      ...HostedTicketEventFields
      description
      repeatsEvery
      affiliateEnabled
      affiliateCommissionPercent
      fanPollingEnabled
      doorsOpenAt
      endsAt
      admissionClosesAt
      salesCloseAt
      hashtags {
        id
        name
      }
      venueAddress {
        addressLine1
        city
        region
        postalCode
        country
      }
      latitude
      longitude
      matchups {
        ...MatchupFields
      }
    }
    hostedTicketTiers(input: $input) {
      id
      label
      price
      capacity
    }
  }
  ${HostedTicketEventFields}
  ${MatchupFields}
`;
